import {makeAutoObservable, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';

import apiService from '../transport/apiService';

import type {RootStore} from './rootStore';

export class WishlistStore {
  rootStore: RootStore;
  wishlist: any = null;
  // A wishlist is just a list of entries of MFL product ID and barcode.
  // {
  //     "id": "6307070f46391a573d017993", <- wishlist entry id
  //     "product_code": "09319020007144", <- barcode
  //     "product_id": "59f68997e1272f2079013850", <- MFL product
  // }
  wishlistEntries: any[] = [];
  // The actual products (search result for barcodes in wishlist)
  wishlistProducts: any[] = [];
  loadingWishlistProducts = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    makeLoggable(this);
  }

  reset = () => {
    runInAction(() => {
      this.wishlist = null;
      this.wishlistEntries = [];
      this.wishlistProducts = [];
      this.loadingWishlistProducts = false;
    });
  };

  isInWishlist = (barcode: any) =>
    !!this.wishlistProducts &&
    !!this.wishlistProducts.find((item: any) => item.product_code === barcode);

  // MFL allows multiple lists, we are allowing only one
  loadWishlistForUser = async () => {
    let wishlists: any[] = [];
    let entries: any[] = [];
    try {
      wishlists = await apiService.getWishlists();
    } catch (error) {
      console.log('Unable to load wishlists for user');
      return;
    }
    // Create a default wishlist if the user doesn't have one already
    if (wishlists.length === 0) {
      try {
        const defaultList = await apiService.createWishlist('My wishlist');
        wishlists = [defaultList];
      } catch (error) {
        console.log('Unable to create default wishlist for user');
        return;
      }
    }
    // The entries in the list (product/barcode)
    try {
      entries = await apiService.getItemsInWishlist(wishlists[0].id);
    } catch (error) {
      console.log('Unable to get entries for wishlist', wishlists[0].id);
    }

    runInAction(() => {
      this.wishlist = wishlists[0];
      this.wishlistEntries = entries;
    });

    if (entries.length !== 0) {
      await this.loadWishlistProducts(entries);
    }
  };

  // Load the MFL products of the selected wishlist
  loadWishlistProducts = async (entries: any[]) => {
    if (!entries) {
      entries = this.wishlistEntries;
    }
    runInAction(() => {
      this.wishlistProducts = [];
      this.loadingWishlistProducts = true;
    });
    const barcodes = entries.map(item => item.product_code);
    if (barcodes.length) {
      const data = (await apiService.searchByBarcode(
        barcodes,
      )) as unknown as any[];
      runInAction(() => (this.wishlistProducts = data));
    } else {
      runInAction(() => (this.wishlistProducts = []));
    }
    runInAction(() => (this.loadingWishlistProducts = false));
  };

  // Contents are ok, we've added the item in already.
  // The items need to be updated as each entry has
  // its own ID which is required to remove it from the
  // wishlist.
  addItemToWishlist = (item: {product_id: any; product_code: any}) => {
    apiService
      .addItemToWishlist(this.wishlist.id, item.product_id, item.product_code)
      .then(async () => {
        let entries = [...this.wishlistEntries];
        try {
          // reload the entries for the wishlist as a new entry ID will be generated for the entry.
          entries = await apiService.getItemsInWishlist(this.wishlist.id);
          const newContents = [...this.wishlistProducts];
          newContents.push(item);
          runInAction(() => {
            this.wishlistProducts = newContents;
            this.wishlistEntries = entries;
          });
        } catch (error) {
          console.log('unable to retrieve wishlist entries after adding item');
        }
      });
  };

  removeItemFromWishlist = (item: {product_code: any}) => {
    let found: any = null;
    let entries = [...this.wishlistEntries];
    let products = [...this.wishlistProducts];
    // Split the existing entries to those not matching the item and the item we're looking for
    this.wishlistEntries.forEach(entry => {
      if (entry.product_code === item.product_code) {
        found = entry;
      } else {
        entries.push(entry);
      }
    });
    // Remove the product from the list
    products = products.filter(
      product => product.product_code !== item.product_code,
    );

    if (found) {
      apiService
        .removeItemFromWishlist(this.wishlist.id, found.id, found.product_code)
        .then(() =>
          runInAction(() => {
            this.wishlistEntries = entries;
            this.wishlistProducts = products;
          }),
        )
        .catch(err =>
          console.error(
            'Error: removing product from wishlist that is not in the list',
            err,
          ),
        );
    }
  };
}
